import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, Gift, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const useLimitOffer = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/me/limit-offer").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  return d;
};

const hoursLeft = (iso) => Math.max(1, Math.ceil((new Date(iso) - Date.now()) / 3600000));

export const LimitOfferDialog = () => {
  const { t } = useI18n();
  const nav = useNavigate();
  const [kind, setKind] = useState(null);
  const [d, setD] = useState(null);
  useEffect(() => {
    const on = (e) => api.get("/me/limit-offer").then((r) => { setD(r.data); setKind(e.detail); }).catch(() => {});
    window.addEventListener("cx-limit", on);
    return () => window.removeEventListener("cx-limit", on);
  }, []);
  const open = !!kind && !!d && !d.owner;
  const sell = d && !d.demo;
  const go = () => { setKind(null); nav("/credits"); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && setKind(null)}>
      <DialogContent className="bg-[#0b0e1a] border-[#ff3d8b]/30 max-w-md" data-testid="limit-offer-dialog">
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Gauge className="w-5 h-5 text-[#ff3d8b]" />{t("lim.title")}</DialogTitle></DialogHeader>
        {d && <div className="space-y-4">
          <p className="text-sm text-slate-300" data-testid="limit-offer-reason">
            {t(kind === "live" ? "lim.reason_live" : "lim.reason_import").replace("{n}", d.limits[kind] ?? "")}
          </p>
          {sell && !d.paid && <p className="text-sm text-slate-400" data-testid="limit-offer-double">{t("lim.double").replace("{i}", d.base.import * 2).replace("{l}", d.base.live * 2)}</p>}
          {sell && d.offer && (
            <div className="rounded-xl border border-[#22e3a1]/40 bg-[#22e3a1]/10 p-4" data-testid="limit-offer-bonus">
              <div className="flex items-center gap-2 text-[#22e3a1] font-display text-lg"><Gift className="w-5 h-5" />{t("lim.bonus").replace("{p}", d.offer.pct)}</div>
              <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1"><Clock className="w-3.5 h-3.5" />{t("lim.expires").replace("{h}", hoursLeft(d.offer.expires_at))}</div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => setKind(null)} data-testid="limit-offer-later" className="rounded-full px-4 py-2.5 text-sm text-slate-300 border border-white/15">{t("lim.later")}</button>
            {sell && <button onClick={go} data-testid="limit-offer-cta" className="cx-btn rounded-full px-5 py-2.5 text-sm font-semibold">{t("lim.cta")}</button>}
          </div>
        </div>}
      </DialogContent>
    </Dialog>
  );
};

export const LimitOfferBanner = ({ offer }) => {
  const { t } = useI18n();
  if (!offer) return null;
  return (
    <div className="mt-6 rounded-2xl border border-[#22e3a1]/40 bg-[#22e3a1]/10 p-4 flex flex-wrap items-center gap-3" data-testid="limit-offer-banner">
      <Gift className="w-5 h-5 text-[#22e3a1]" />
      <span className="text-sm text-white font-semibold">{t("lim.banner").replace("{p}", offer.pct)}</span>
      <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{t("lim.expires").replace("{h}", hoursLeft(offer.expires_at))}</span>
    </div>
  );
};
